import { useState, useEffect, useCallback, useRef } from "react";
import { Table } from "@tanstack/react-table";
import { TableConfig } from "../models/table-configuration.dto";
import { getAccessToken } from "../services/utils/app.data";
import ENDPOINTS from "../services/utils/endpoints";
import useUserStore from "../state-management/useUserStore";
import { tableConfigCache } from "../services/tableConfigCache";

// In-memory storage for all table configurations (synced with cache)
let globalTableConfigs: Record<string, TableConfig> = {};

export const useTableConfiguration = (
	tableId: string,
	table: Table<any> | null,
	defaultRowHeight: number = 65,
) => {
	const { currentUser } = useUserStore();
	const [rowHeight, setRowHeight] = useState(defaultRowHeight);
	const [isSaving, setIsSaving] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const hasLoadedRef = useRef(false);
	const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

	// ✅ Use refs to track the latest table state without causing re-renders
	const latestTableStateRef = useRef<{
		columnSizing: Record<string, number>;
		columnVisibility: Record<string, boolean>;
		columnOrder: string[];
	}>({
		columnSizing: {},
		columnVisibility: {},
		columnOrder: [],
	});

	// ✅ Update refs whenever table state changes
	useEffect(() => {
		if (!table) return;

		latestTableStateRef.current = {
			columnSizing: table.getState().columnSizing,
			columnVisibility: table.getState().columnVisibility,
			columnOrder: table.getAllColumns().map((col) => col.id),
		};
	}, [
		table?.getState().columnSizing,
		table?.getState().columnVisibility,
		table,
	]);

	// Get current table configuration
	const getCurrentConfig = useCallback((): TableConfig => {
		if (!table) {
			return {
				rowHeight,
				columnWidths: {},
				columnOrder: [],
				columnVisibility: {},
			};
		}

		return {
			rowHeight,
			columnWidths: latestTableStateRef.current.columnSizing,
			columnOrder: latestTableStateRef.current.columnOrder,
			columnVisibility: latestTableStateRef.current.columnVisibility,
		};
	}, [table, rowHeight]);

	// Update the global config for this table
	const updateGlobalConfig = useCallback(() => {
		if (!table) return;
		const config = getCurrentConfig();
		globalTableConfigs[tableId] = config;

		// Update cache with new table config
		tableConfigCache.updateTableConfig(tableId, config);
	}, [tableId, getCurrentConfig, table]);

	// ✅ IMPROVED: Fetch function for cache to use
	const fetchConfigurationsFromAPI = useCallback(async (): Promise<
		Record<string, TableConfig>
	> => {
		const response = await fetch(ENDPOINTS.USER.UPDATE_SELF, {
			headers: {
				Authorization: `Bearer ${getAccessToken()}`,
			},
		});

		if (!response.ok) {
			const errorText = await response.text();
			console.error("❌ Load failed with status:", response.status);
			console.error("❌ Error response:", errorText);
			throw new Error("Failed to load user data");
		}

		const responseData = await response.json();
		const userData = responseData.data || responseData;
		const customPrefs = userData.customPrefs;

		return customPrefs?.tableConfigurations || {};
	}, []);

	const saveAllConfigurations = useCallback(async () => {
		if (!currentUser?.user?.id) {
			console.warn("No user ID available, skipping save");
			return;
		}

		if (!hasLoadedRef.current) {
			console.warn("⏭️ Not loaded yet, skipping save");
			return;
		}

		setIsSaving(true);
		try {
			updateGlobalConfig();

			const payload = {
				customPrefs: {
					tableConfigurations: globalTableConfigs,
				},
			};

			const response = await fetch(ENDPOINTS.USER.UPDATE_SELF, {
				method: "PATCH",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${getAccessToken()}`,
				},
				body: JSON.stringify(payload),
			});

			if (!response.ok) {
				const errorText = await response.text();
				console.error("❌ Save failed with status:", response.status);
				console.error("❌ Error response:", errorText);
				throw new Error(
					`Failed to save table configurations: ${response.status}`,
				);
			}

			const result = await response.json();

			// ✅ Update cache after successful save
			tableConfigCache.updateCache(globalTableConfigs);

			// ✅ Verify the data was actually saved
			if (!result.customPrefs?.tableConfigurations) {
				console.warn(
					"⚠️ Warning: Backend response doesn't include tableConfigurations",
				);
			}

			return result;
		} catch (error) {
			console.error("❌ Error saving table configurations:", error);
			throw error;
		} finally {
			setIsSaving(false);
		}
	}, [currentUser?.user?.id, updateGlobalConfig]);

	// ✅ IMPROVED: Use cache manager for loading
	const loadAllConfigurations = useCallback(
		async (forceRefresh: boolean = false) => {
			if (hasLoadedRef.current && !forceRefresh) {
				return globalTableConfigs;
			}

			if (!currentUser?.user?.id) {
				console.warn("No user ID available, skipping load");
				return;
			}

			if (!table) {
				console.warn("Table not ready, skipping load");
				return;
			}

			setIsLoading(true);
			try {
				// ✅ Use cache manager - it handles caching, deduplication, and TTL
				const configs = await tableConfigCache.getConfigurations(
					fetchConfigurationsFromAPI,
					forceRefresh,
				);

				globalTableConfigs = configs;

				const thisTableConfig = globalTableConfigs[tableId];

				if (thisTableConfig) {
					setRowHeight(thisTableConfig.rowHeight);

					if (
						thisTableConfig.columnWidths &&
						Object.keys(thisTableConfig.columnWidths).length > 0
					) {
						table.setColumnSizing(thisTableConfig.columnWidths);
					}

					if (thisTableConfig.columnVisibility) {
						table.setColumnVisibility(thisTableConfig.columnVisibility);
					}

					if (
						thisTableConfig.columnOrder &&
						thisTableConfig.columnOrder.length > 0
					) {
						table.setColumnOrder(thisTableConfig.columnOrder);
					}

					hasLoadedRef.current = true;
				} else {
					hasLoadedRef.current = true;
				}

				return configs;
			} catch (error) {
				console.error("❌ Error loading table configurations:", error);
				hasLoadedRef.current = true;
				return null;
			} finally {
				setIsLoading(false);
			}
		},
		[currentUser?.user?.id, table, tableId, fetchConfigurationsFromAPI],
	);

	const resetConfiguration = useCallback(() => {
		if (!table) return;

		setRowHeight(defaultRowHeight);
		table.resetColumnSizing();
		table.resetColumnVisibility();

		delete globalTableConfigs[tableId];

		// Clear from cache
		tableConfigCache.clearTable(tableId);

		saveAllConfigurations();
	}, [table, defaultRowHeight, tableId, saveAllConfigurations]);

	const resetAllConfigurations = useCallback(async () => {
		if (!table) return;

		globalTableConfigs = {};
		setRowHeight(defaultRowHeight);
		table.resetColumnSizing();
		table.resetColumnVisibility();

		try {
			await fetch(ENDPOINTS.USER.UPDATE_SELF, {
				method: "PATCH",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${getAccessToken()}`,
				},
				body: JSON.stringify({ customPrefs: null }),
			});

			// Invalidate entire cache
			tableConfigCache.invalidate();
		} catch (error) {
			console.error("❌ Error clearing table configurations:", error);
		}
	}, [table, defaultRowHeight]);

	// ✅ Separate effect specifically for row height changes
	useEffect(() => {
		if (!currentUser?.user?.id || !hasLoadedRef.current) {
			return;
		}

		if (saveTimeoutRef.current) {
			clearTimeout(saveTimeoutRef.current);
		}

		saveTimeoutRef.current = setTimeout(() => {
			saveAllConfigurations();
		}, 1000);

		return () => {
			if (saveTimeoutRef.current) {
				clearTimeout(saveTimeoutRef.current);
			}
		};
	}, [rowHeight, currentUser?.user?.id, saveAllConfigurations]);

	// ✅ Watch for column changes and trigger saves
	useEffect(() => {
		if (!currentUser?.user?.id || !table || !hasLoadedRef.current) {
			return;
		}

		if (saveTimeoutRef.current) {
			clearTimeout(saveTimeoutRef.current);
		}

		saveTimeoutRef.current = setTimeout(() => {
			saveAllConfigurations();
		}, 2000);

		return () => {
			if (saveTimeoutRef.current) {
				clearTimeout(saveTimeoutRef.current);
			}
		};
	}, [
		JSON.stringify(table?.getState().columnSizing),
		JSON.stringify(table?.getState().columnVisibility),
		currentUser?.user?.id,
		table,
		saveAllConfigurations,
	]);

	// Manual trigger for immediate saves
	const triggerSave = useCallback(() => {
		if (hasLoadedRef.current) {
			saveAllConfigurations();
		}
	}, [saveAllConfigurations]);

	// Refresh cache
	const refreshCache = useCallback(() => {
		hasLoadedRef.current = false;
		return loadAllConfigurations(true);
	}, [loadAllConfigurations]);

	// Get cache stats for debugging
	const getCacheStats = useCallback(() => {
		return tableConfigCache.getStats();
	}, []);

	return {
		rowHeight,
		setRowHeight,
		saveAllConfigurations,
		loadAllConfigurations,
		resetConfiguration,
		resetAllConfigurations,
		getCurrentConfig,
		getAllConfigs: () => globalTableConfigs,
		triggerSave,
		refreshCache,
		getCacheStats,
		isSaving,
		isLoading,
	};
};
